/**
 * @krisspy-file
 * @type page
 * @name "CategoryForm"
 * @title "Category Form"
 * @description "Create or edit a category"
 * @routes ["/categories/create", "/categories/:id/edit"]
 * @layout "DashboardLayout"
 * @flowName "category-management"
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Loader2,
  Tag,
  AlertCircle,
  Languages,
  Trash2,
  X,
  Upload
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import {
  getCategoryById,
  createCategory,
  updateCategory,
  deleteCategory,
  upsertCategoryTranslation,
  getCategoryTranslations,
  deleteCategoryTranslation
} from '../services/categories.service';
import { useAuth } from '../contexts/AuthContext';
import { uploadCategoryImage } from '../lib/storage';
import { useLanguage } from '../contexts/LanguageContext';
import type { Database } from '../lib/database.types';
import LoadingPlaceholder from '../components/LoadingPlaceholder';

type CategoryInsert = Database['public']['Tables']['categories']['Insert'];
type CategoryUpdate = Database['public']['Tables']['categories']['Update'];

export default function CategoryForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { currentLanguage, availableLanguages, defaultLanguage } = useLanguage();
  const { merchantId, locationIds, isPlatformAdmin } = useAuth();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [activeLanguageTab, setActiveLanguageTab] = useState(defaultLanguage);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    category_name: '',
    category_slug: '',
    description: '',
    picture_url: '',
    display_order: 0,
    show_picture: false,
    active: true
  });

  // Translations for each language
  const [translations, setTranslations] = useState<Record<string, {
    category_name: string;
    description: string;
  }>>({});

  useEffect(() => {
    if (isEditMode && id) {
      loadCategory();
    }
  }, [id, defaultLanguage]);

  async function loadCategory() {
    if (!id) return;

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getCategoryById(id, {
      merchantId,
      locationIds,
      isPlatformAdmin
    }, defaultLanguage, defaultLanguage);

    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }

    if (data) {
      setFormData({
        category_name: data.category_name || '',
        category_slug: data.category_slug || '',
        description: data.description || '',
        picture_url: data.picture_url || '',
        display_order: data.display_order || 0,
        show_picture: data.show_picture || false,
        active: data.active !== null ? data.active : true
      });

      // Load all translations (excluding default language - that's in the main table)
      const { data: translationsData } = await getCategoryTranslations(id);
      if (translationsData) {
        const translationsMap: Record<string, { category_name: string; description: string }> = {};
        translationsData.forEach((t) => {
          // Only load translations for non-default languages
          if (t.language_code !== defaultLanguage) {
            translationsMap[t.language_code] = {
              category_name: t.category_name || '',
              description: t.description || ''
            };
          }
        });
        setTranslations(translationsMap);
      }
    }

    setLoading(false);
  }

  function generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function handleNameChange(name: string) {
    setFormData(prev => ({
      ...prev,
      category_name: name,
      category_slug: prev.category_slug || generateSlug(name)
    }));
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};

    if (!formData.category_name.trim()) {
      errors.category_name = 'Category name is required';
    }

    if (!formData.category_slug.trim()) {
      errors.category_slug = 'Category slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.category_slug)) {
      errors.category_slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleImageUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const publicUrl = await uploadCategoryImage(file, merchantId || 'default');
      setFormData(prev => ({ ...prev, picture_url: publicUrl }));
    } catch (error: any) {
      console.error('Error uploading image:', error);
      showToast(error.message || 'Failed to upload image', 'error');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  function handleRemoveImage() {
    setFormData(prev => ({ ...prev, picture_url: '' }));
  }

  async function handleDelete() {
    if (!id) return;

    setDeleting(true);
    setDeleteError(null);

    const { error: deleteErr } = await deleteCategory(id);

    if (deleteErr) {
      setDeleteError(deleteErr);
      setDeleting(false);
      return;
    }

    showToast(t('categories.deleteSuccess', 'Category deleted successfully'), 'success');
    navigate('/categories');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let categoryId = id;

      // Admin UI language only affects chrome — content is always in the merchant's default language
      const isEditingDefaultLanguage = true;

      if (isEditingDefaultLanguage) {
        // Editing default language - save to main table
        if (isEditMode && id) {
          // Update existing category
          const updateData: CategoryUpdate = {
            category_name: formData.category_name,
            category_slug: formData.category_slug,
            description: formData.description || null,
            picture_url: formData.picture_url || null,
            display_order: formData.display_order,
            show_picture: formData.show_picture,
            active: formData.active,
            updated_at: new Date().toISOString()
          };

          const { error: updateError } = await updateCategory(id, updateData);

          if (updateError) {
            showToast('Failed to update category: ' + updateError, 'error');
            setSaving(false);
            return;
          }

          // Clean up any stale default-language translation row so it can't
          // override the freshly-updated main-table value on later reads.
          if (defaultLanguage) {
            await deleteCategoryTranslation(id, defaultLanguage);
          }
        } else {
          // Create new category
          if (!merchantId) {
            showToast(t('categories.noMerchantAccess'), 'error');
            setSaving(false);
            return;
          }

          const insertData: CategoryInsert = {
            merchant_id: merchantId,
            category_name: formData.category_name,
            category_slug: formData.category_slug,
            description: formData.description || null,
            picture_url: formData.picture_url || null,
            display_order: formData.display_order,
            show_picture: formData.show_picture,
            active: formData.active
          };

          const { data: newCategory, error: createError } = await createCategory(insertData);

          if (createError) {
            showToast('Failed to create category: ' + createError, 'error');
            setSaving(false);
            return;
          }

          if (!newCategory) {
            navigate('/categories');
            return;
          }

          categoryId = newCategory.id;
        }

        // Save translations from translation tabs (skip default language - that's the main table).
        // A language that was loaded/edited but left empty is deleted so clearing a
        // translation persists instead of leaving the previous row in place.
        if (categoryId) {
          for (const lang of availableLanguages) {
            if (lang.code === defaultLanguage) continue;

            const translation = translations[lang.code];
            if (translation && (translation.category_name || translation.description)) {
              await upsertCategoryTranslation(categoryId, lang.code, {
                category_name: translation.category_name || '',
                description: translation.description || null
              });
            } else if (translation) {
              await deleteCategoryTranslation(categoryId, lang.code);
            }
          }
        }
      } else {
        // Editing translation - save to translation table
        if (!isEditMode || !id) {
          showToast('Cannot create new category in non-default language. Please switch to ' + defaultLanguage.toUpperCase(), 'warning');
          setSaving(false);
          return;
        }

        // Save translation
        await upsertCategoryTranslation(id, currentLanguage, {
          category_name: formData.category_name || '',
          description: formData.description || null
        });

        categoryId = id;
      }

      showToast(isEditMode ? 'Category updated successfully' : 'Category created successfully', 'success');
      navigate(categoryId ? `/categories/${categoryId}` : '/categories');
    } catch (err) {
      console.error('Error saving category:', err);
      showToast('An error occurred while saving the category', 'error');
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="flex items-center gap-md">
          <button onClick={() => navigate('/categories')} className="btn-icon">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-primary">{t('common.loading')}</h1>
        </div>
        <LoadingPlaceholder />
      </div>
    );
  }

  if (error && isEditMode) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="flex items-center gap-md">
          <button onClick={() => navigate('/categories')} className="btn-icon">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-3xl font-bold text-primary">{t('common.error')}</h1>
        </div>
        <div className="card">
          <div className="text-center py-2xl">
            <div className="w-16 h-16 rounded-full bg-danger/10 mx-auto mb-lg flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-danger" />
            </div>
            <h3 className="text-lg font-semibold text-primary mb-sm">{t('categories.notFound')}</h3>
            <p className="text-secondary mb-xl">{error}</p>
            <button onClick={() => navigate('/categories')} className="btn btn-primary">
              {t('categories.backToCategories')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      <div className="page-header">
        <div className="page-header-title flex items-center gap-md">
          <button
            onClick={() => navigate('/categories')}
            className="btn-icon"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-primary mb-xs">
              {isEditMode ? t('categories.editCategory') : t('categories.createCategory')}
            </h1>
            <p className="text-secondary">
              {isEditMode ? t('categories.updateInfo') : t('categories.addNew')}
            </p>
          </div>
        </div>
        {isEditMode && (
          <div className="page-header-actions">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="btn btn-danger-outline"
            >
              <Trash2 className="w-4 h-4" />
              {t('common.delete', 'Delete')}
            </button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-xl">
        <div className="lg:col-span-2 space-y-xl">
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg">{t('categories.basicInformation')}</h2>

            {error && !isEditMode && (
              <div className="mb-lg p-md bg-danger/10 border border-danger/20 rounded-lg">
                <p className="text-danger text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-lg">
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('categories.categoryName')} *
                </label>
                <input
                  type="text"
                  value={formData.category_name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`input w-full ${validationErrors.category_name ? 'border-danger' : ''}`}
                  placeholder={t('categories.placeholderName')}
                  required
                />
                {validationErrors.category_name && (
                  <p className="text-danger text-sm mt-xs">{validationErrors.category_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('categories.categorySlug')} *
                </label>
                <input
                  type="text"
                  value={formData.category_slug}
                  onChange={(e) => setFormData({ ...formData, category_slug: e.target.value })}
                  className={`input w-full ${validationErrors.category_slug ? 'border-danger' : ''}`}
                  placeholder={t('categories.placeholderSlug')}
                  required
                />
                {validationErrors.category_slug && (
                  <p className="text-danger text-sm mt-xs">{validationErrors.category_slug}</p>
                )}
                <p className="text-xs text-secondary mt-xs">
                  {t('categories.slugHelp')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('categories.categoryDescription')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input w-full"
                  rows={4}
                  placeholder={t('categories.placeholderDescription')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('categories.pictureUrl')}
                </label>

                {formData.picture_url ? (
                  <div className="relative group">
                    <img
                      src={formData.picture_url}
                      alt="Category"
                      className="w-full h-48 object-contain rounded-lg"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1 bg-danger text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    uploading
                      ? 'border-blue/50 bg-blue/5'
                      : 'border-primary/20 hover:border-blue/50 hover:bg-blue/5'
                  }`}>
                    <div className="flex flex-col items-center justify-center py-lg">
                      {uploading ? (
                        <>
                          <Loader2 className="w-8 h-8 text-blue animate-spin mb-sm" />
                          <p className="text-sm text-secondary">{t('common.uploading', 'Uploading...')}</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-secondary mb-sm" />
                          <p className="text-sm text-secondary">
                            {t('categories.uploadImage', 'Click to upload an image')}
                          </p>
                          <p className="text-xs text-secondary mt-xs">PNG, JPG, WebP (max 10MB)</p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                )}

                {!formData.picture_url && !uploading && (
                  <div className="mt-sm">
                    <p className="text-xs text-secondary mb-xs">{t('categories.orPasteUrl', 'Or paste an image URL')}</p>
                    <input
                      type="url"
                      value={formData.picture_url}
                      onChange={(e) => setFormData({ ...formData, picture_url: e.target.value })}
                      className="input w-full text-sm"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Translations Section */}
          {availableLanguages.length > 1 && (
            <div className="card">
              <div className="flex items-center gap-md mb-lg">
                <Languages className="w-5 h-5 text-blue" />
                <h2 className="text-lg font-semibold text-primary">{t('categories.translations')}</h2>
              </div>

              {/* Language Tabs */}
              <div className="flex gap-sm mb-lg border-b border-primary/10 overflow-x-auto">
                {availableLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => setActiveLanguageTab(lang.code)}
                    className={`px-md py-sm text-sm font-medium whitespace-nowrap transition-colors ${
                      activeLanguageTab === lang.code
                        ? 'text-blue border-b-2 border-blue'
                        : 'text-secondary hover:text-primary'
                    }`}
                  >
                    {lang.flag} {lang.label}
                  </button>
                ))}
              </div>

              {/* Translation Fields */}
              <div className="space-y-lg">
                {activeLanguageTab === defaultLanguage ? (
                  /* Show main table data for default language */
                  <div className="p-md bg-blue/5 border border-blue/20 rounded-lg">
                    <p className="text-sm text-blue">
                      <strong>{t('categories.categoryName')}:</strong> {formData.category_name || <em className="text-secondary">({t('common.none')})</em>}
                    </p>
                    <p className="text-sm text-blue mt-sm">
                      <strong>{t('categories.categoryDescription')}:</strong> {formData.description || <em className="text-secondary">({t('common.none')})</em>}
                    </p>
                    <p className="text-xs text-secondary mt-md">
                      This is the main language. Edit the fields above in the form to change these values.
                    </p>
                  </div>
                ) : (
                  /* Show translation fields for other languages */
                  <>
                    <div>
                      <label className="block text-sm font-medium text-primary mb-sm">
                        {t('categories.categoryName')} ({activeLanguageTab.toUpperCase()})
                      </label>
                      <input
                        type="text"
                        value={translations[activeLanguageTab]?.category_name || ''}
                        onChange={(e) => setTranslations({
                          ...translations,
                          [activeLanguageTab]: {
                            ...translations[activeLanguageTab],
                            category_name: e.target.value,
                            description: translations[activeLanguageTab]?.description || ''
                          }
                        })}
                        className="input w-full"
                        placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                      />
                      <p className="text-xs text-secondary mt-xs">
                        {t('categories.translationHelper')}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary mb-sm">
                        {t('categories.categoryDescription')} ({activeLanguageTab.toUpperCase()})
                      </label>
                      <textarea
                        value={translations[activeLanguageTab]?.description || ''}
                        onChange={(e) => setTranslations({
                          ...translations,
                          [activeLanguageTab]: {
                            ...translations[activeLanguageTab],
                            category_name: translations[activeLanguageTab]?.category_name || '',
                            description: e.target.value
                          }
                        })}
                        className="input w-full"
                        rows={4}
                        placeholder={`Add the ${availableLanguages.find(l => l.code === activeLanguageTab)?.label} version`}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-xl">
          <div className="card">
            <h3 className="text-lg font-semibold text-primary mb-lg">{t('categories.settings')}</h3>
            <div className="space-y-lg">
              <div>
                <label className="block text-sm font-medium text-primary mb-sm">
                  {t('categories.displayOrder')}
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="input w-full"
                  min="0"
                />
                <p className="text-xs text-secondary mt-xs">
                  {t('categories.displayOrderHelp')}
                </p>
              </div>

              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.show_picture}
                    onChange={(e) => setFormData({ ...formData, show_picture: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('categories.showPicture')}</span>
                </label>
              </div>

              <div>
                <label className="flex items-center gap-md">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm font-medium text-primary">{t('common.active')}</span>
                </label>
              </div>
            </div>
          </div>

          <div className="card bg-tertiary">
            <div className="flex items-start gap-md">
              <Tag className="w-5 h-5 text-blue flex-shrink-0 mt-xs" />
              <div>
                <h4 className="text-sm font-semibold text-primary mb-xs">{t('categories.categoryTips')}</h4>
                <p className="text-xs text-secondary">
                  {t('categories.description')}
                </p>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary w-full"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? t('categories.updateCategory') : t('categories.createCategory')}
              </>
            )}
          </button>
        </div>
      </form>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !deleting && setShowDeleteModal(false)}
          />
          <div className="relative bg-primary rounded-xl shadow-2xl max-w-md w-full mx-4 p-xl animate-fade-in">
            <button
              onClick={() => !deleting && setShowDeleteModal(false)}
              className="absolute top-4 right-4 btn-icon"
              disabled={deleting}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-danger/10 mx-auto mb-lg flex items-center justify-center">
                <Trash2 className="w-8 h-8 text-danger" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-sm">{t('categories.deleteCategory', 'Delete Category')}</h3>
              <p className="text-secondary mb-xl">
                {t('categories.deleteConfirm', {
                  name: formData.category_name,
                  defaultValue: `Are you sure you want to delete "${formData.category_name}"? This action cannot be undone.`
                })}
              </p>

              {deleteError && (
                <div className="mb-lg p-md bg-danger/10 border border-danger/20 rounded-lg">
                  <p className="text-danger text-sm">{deleteError}</p>
                </div>
              )}

              <div className="flex gap-md justify-center">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn btn-secondary"
                  disabled={deleting}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-danger"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.deleting', 'Deleting...')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      {t('categories.deleteCategory', 'Delete Category')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
